/**
 * 性能分析 v2：一按钮一条业务耗时（写入 perf_action_log，与旧 perf_log 无关）
 */
import { getActiveBrandId } from "./brandDeviceFilter";
import { mergeTrackExtra } from "./trackEvent";
import { scheduleMedalPendingPull } from "./medalPendingPull";

export type PerfActionFeature =
  | "connect"
  | "enter_subapp"
  | "enter_module"
  | "virtual_keyboard"
  | "lighting"
  | "change_key"
  | "macro"
  | "performance"
  | "board"
  | "trigger"
  | "dpi"
  | "screen"
  | "quick_config"
  | "other"
  | "route"
  | string;

export type PerfActionDeviceContext = {
  brand?: string;
  solutionProvider?: string;
  deviceModel?: string;
  deviceType?: string;
  connectionType?: "USB" | "2.4G";
};

export type PerfActionPayload = PerfActionDeviceContext & {
  feature: PerfActionFeature;
  actionId: string;
  actionLabel?: string;
  moduleId?: string;
  durationMs: number;
  success?: boolean;
  clientScope?: "web" | "desktop";
  appVersion?: string;
  userId?: number;
  sessionId?: string;
  extra?: Record<string, unknown>;
};

const SESSION_KEY = "EVENT_LOG_SESSION_ID";
const DEVICE_CTX_KEY = "PERF_ACTION_DEVICE_CTX";

const getOrCreateSessionId = () => {
  if (typeof localStorage === "undefined") return "";
  let sessionId = localStorage.getItem(SESSION_KEY) || "";
  if (!sessionId) {
    sessionId = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    localStorage.setItem(SESSION_KEY, sessionId);
  }
  return sessionId;
};

const parseUserId = (): number | undefined => {
  try {
    const raw = localStorage.getItem("userInfo");
    const userInfo = raw ? JSON.parse(raw) : null;
    const idVal = userInfo?.id;
    if (typeof idVal === "number") return idVal;
    if (typeof idVal === "string" && idVal.trim() !== "") {
      const parsed = Number(idVal);
      if (!Number.isNaN(parsed)) return parsed;
    }
  } catch {
    // ignore
  }
  return undefined;
};

const detectClientScope = (): "web" | "desktop" => {
  if (typeof window === "undefined") return "web";
  if ((window as any).electronBridge || (window as any).electronAPI) {
    return "desktop";
  }
  if (
    typeof navigator !== "undefined" &&
    /electron/i.test(navigator.userAgent)
  ) {
    return "desktop";
  }
  return "web";
};

const resolveAuthHeader = (): string | undefined => {
  try {
    if (typeof localStorage === "undefined") return undefined;
    const token = localStorage.getItem("token")?.trim();
    return token ? `Bearer ${token}` : undefined;
  } catch {
    return undefined;
  }
};

const resolveBrand = (explicit?: string): string | undefined => {
  if (explicit && explicit.trim()) return explicit.trim().toLowerCase();
  try {
    const id = getActiveBrandId().trim().toLowerCase();
    return id || undefined;
  } catch {
    return undefined;
  }
};

/** 连接成功后写入当前设备上下文，后续 measurePerfAction 自动带上 */
export function setPerfActionDeviceContext(
  ctx: PerfActionDeviceContext | null
): void {
  if (typeof sessionStorage === "undefined") return;
  if (!ctx) {
    sessionStorage.removeItem(DEVICE_CTX_KEY);
    return;
  }
  sessionStorage.setItem(DEVICE_CTX_KEY, JSON.stringify(ctx));
}

export function getPerfActionDeviceContext(): PerfActionDeviceContext {
  if (typeof sessionStorage === "undefined") return {};
  const raw = sessionStorage.getItem(DEVICE_CTX_KEY);
  if (!raw) return {};
  try {
    return JSON.parse(raw) as PerfActionDeviceContext;
  } catch {
    return {};
  }
}

/** 上报一条业务操作耗时（失败静默） */
export function logPerfAction(payload: PerfActionPayload): void {
  if (typeof fetch === "undefined") return;
  const ctx = getPerfActionDeviceContext();
  const body = {
    brand: resolveBrand(payload.brand ?? ctx.brand),
    solutionProvider:
      payload.solutionProvider?.trim() || ctx.solutionProvider || undefined,
    deviceModel: payload.deviceModel?.trim() || ctx.deviceModel || undefined,
    deviceType: payload.deviceType?.trim() || ctx.deviceType || undefined,
    connectionType: payload.connectionType || ctx.connectionType || undefined,
    feature: payload.feature,
    actionId: payload.actionId,
    actionLabel: payload.actionLabel,
    moduleId:
      payload.moduleId ||
      (typeof window !== "undefined"
        ? window.location.pathname.replace(/^\//, "") || undefined
        : undefined),
    durationMs: Math.max(0, Math.round(payload.durationMs)),
    success: payload.success !== false,
    clientScope: payload.clientScope ?? detectClientScope(),
    appVersion: payload.appVersion,
    userId: payload.userId ?? parseUserId(),
    sessionId: payload.sessionId ?? getOrCreateSessionId(),
    extra: JSON.stringify(mergeTrackExtra(payload.extra)),
  };
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  const auth = resolveAuthHeader();
  if (auth) headers.Authorization = auth;

  void fetch("/api/v1/perf-actions", {
    method: "POST",
    headers,
    body: JSON.stringify(body),
  }).catch(() => {});

  if (
    body.success &&
    parseUserId() != null &&
    ["connect", "change_key", "lighting", "macro", "board"].includes(
      String(body.feature)
    )
  ) {
    scheduleMedalPendingPull();
  }
}

export type MeasurePerfActionMeta = Omit<
  PerfActionPayload,
  "durationMs" | "success"
> & {
  success?: boolean;
};

/** 包裹一次按钮业务：成功/失败都上报一条 */
export async function measurePerfAction<T>(
  meta: MeasurePerfActionMeta,
  fn: () => Promise<T>
): Promise<T> {
  const startMs = Date.now();
  try {
    const result = await fn();
    logPerfAction({
      ...meta,
      durationMs: Date.now() - startMs,
      success: meta.success !== false,
    });
    return result;
  } catch (err) {
    logPerfAction({
      ...meta,
      durationMs: Date.now() - startMs,
      success: false,
      extra: {
        ...(meta.extra || {}),
        reason: err instanceof Error ? err.message : "perf_action_failed",
      },
    });
    throw err;
  }
}
